<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { type EChartsType } from 'echarts'
import * as echarts from 'echarts'
import type { TimePoint } from '@/types/fiberMonitoring'
import LoaderCenter from '@/components/common/feedback/LoaderCenter.vue'

interface Serie {
  name: string
  points: TimePoint[]
  color?: string
  step?: boolean
}

interface Props {
  series: Serie[]
  title?: string
  loading?: boolean
  error?: string | null
  yMin?: number
  yMax?: number
  // Formate une valeur (axe Y + tooltip). Par défaut : valeur brute.
  valueFormatter?: (v: number) => string
  heightClass?: string
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  loading: false,
  error: null,
  yMin: undefined,
  yMax: undefined,
  valueFormatter: (v: number) => String(v),
  heightClass: 'h-80',
})

const chartRef = ref<HTMLElement | null>(null)
let chartInstance: EChartsType | null = null

function buildOption(): echarts.EChartsCoreOption {
  return {
    tooltip: {
      trigger: 'axis',
      valueFormatter: (value: unknown) =>
        typeof value === 'number' ? props.valueFormatter(value) : '—',
    },
    legend: { type: 'scroll', top: 0 },
    grid: { left: '3%', right: '4%', bottom: '8%', top: props.series.length > 1 ? 32 : 16, containLabel: true },
    xAxis: { type: 'time' },
    yAxis: {
      type: 'value',
      min: props.yMin,
      max: props.yMax,
      axisLabel: { formatter: (value: number) => props.valueFormatter(value) },
    },
    series: props.series.map((s) => ({
      name: s.name,
      type: 'line',
      showSymbol: false,
      step: s.step ? 'end' : undefined,
      ...(s.color ? { color: s.color } : {}),
      // Le remplissage est plus marqué pour les step-lines (statut) : ça donne
      // un bandeau coloré quand UP et un trou bien visible quand DOWN, façon Grafana.
      areaStyle: { opacity: s.step ? 0.35 : 0.08 },
      data: s.points.map((p) => [p.t * 1000, p.v]),
    })),
  }
}

function render() {
  if (!chartRef.value) return
  if (!chartInstance) chartInstance = echarts.init(chartRef.value)
  chartInstance.setOption(buildOption(), true)
}

function handleResize() {
  chartInstance?.resize()
}

onMounted(() => {
  render()
  window.addEventListener('resize', handleResize)
})

watch(() => props.series, render, { deep: true })

onBeforeUnmount(() => {
  chartInstance?.dispose()
  chartInstance = null
  window.removeEventListener('resize', handleResize)
})
</script>

<template>
  <div>
    <h3 v-if="title" class="text-center font-semibold mb-2">{{ title }}</h3>
    <div class="relative w-full" :class="heightClass">
      <div ref="chartRef" class="absolute inset-0"></div>
      <div
        v-if="error"
        class="absolute inset-0 flex items-center justify-center bg-base-100/80 rounded-md"
      >
        <div class="alert alert-error max-w-md">
          <span>{{ error }}</span>
        </div>
      </div>
      <div
        v-else-if="loading"
        class="absolute inset-0 flex items-center justify-center bg-base-100/80 rounded-md"
      >
        <LoaderCenter />
      </div>
    </div>
  </div>
</template>

<style scoped></style>
